import { useEffect, useRef, useState, useCallback } from "react";
import { fetchGoogleMapsApiKey } from "../../services/googleMapsConfig";
import { loadGoogleMapsJs } from "../../services/googleMapsLoader";
import {
  loadLeaflet,
  createLeafletSatelliteMap,
  createLeafletStreetMap,
} from "../../services/leafletMapFallback";

const DEFAULT_CENTER = { lat: 38.84084, lng: 0.10569 };
const MAP_HEIGHT_DEFAULT = 260;

function parseCoord(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function googleMapHasAuthError(container) {
  return Boolean(container?.querySelector?.(".gm-err-container, .gm-err-message"));
}

/**
 * Visor satélite con pin arrastrable. Google Maps si la clave lo permite;
 * si no (referrer/API), fallback Leaflet + Esri sin perder arrastre ni clic.
 */
export default function PropiedadMapaUbicacion({
  lat,
  lng,
  onCoordsChange,
  fallbackCenter,
  radiusM = null,
  cityView = false,
  /** 'street' = calles (roadmap); 'satellite' = satélite/híbrido */
  mapMode = "satellite",
  active = true,
  isDark = false,
  borderColor = "#334155",
  mapHeight = MAP_HEIGHT_DEFAULT,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const circleLeafletRef = useRef(null);
  const leafletRef = useRef(null);
  const mapsNsRef = useRef(null);
  const engineRef = useRef(null); // 'google' | 'leaflet'
  const onCoordsChangeRef = useRef(onCoordsChange);
  onCoordsChangeRef.current = onCoordsChange;

  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [engineLabel, setEngineLabel] = useState("");

  const latN = parseCoord(lat);
  const lngN = parseCoord(lng);
  const hasPin = latN != null && lngN != null;

  const fallbackLat = parseCoord(fallbackCenter?.lat ?? fallbackCenter?.latitude);
  const fallbackLng = parseCoord(fallbackCenter?.lng ?? fallbackCenter?.longitude);

  const streetView = mapMode === "street" || cityView;
  const zoomConPin = streetView ? 15 : 19;
  const zoomSinPin = fallbackLat != null ? (streetView ? 13 : 13) : 11;

  const destroyMap = useCallback(() => {
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }
    if (circleLeafletRef.current && leafletRef.current?.map) {
      leafletRef.current.map.removeLayer(circleLeafletRef.current);
      circleLeafletRef.current = null;
    }
    if (leafletRef.current) {
      leafletRef.current.destroy();
      leafletRef.current = null;
    }
    mapRef.current = null;
    markerRef.current = null;
    mapsNsRef.current = null;
    engineRef.current = null;
    if (containerRef.current) containerRef.current.innerHTML = "";
  }, []);

  const syncRadiusCircle = useCallback(() => {
    const r = Number(radiusM);
    const show = Number.isFinite(r) && r > 0;
    const la = hasPin ? latN : fallbackLat;
    const ln = hasPin ? lngN : fallbackLng;
    if (!show || la == null || ln == null) {
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      if (circleLeafletRef.current && leafletRef.current?.map) {
        leafletRef.current.map.removeLayer(circleLeafletRef.current);
        circleLeafletRef.current = null;
      }
      return;
    }
    if (engineRef.current === "google" && mapRef.current && mapsNsRef.current) {
      const maps = mapsNsRef.current;
      const center = { lat: la, lng: ln };
      if (!circleRef.current) {
        circleRef.current = new maps.Circle({
          map: mapRef.current,
          center,
          radius: r,
          fillColor: "#4f46e5",
          fillOpacity: 0.18,
          strokeColor: "#4f46e5",
          strokeWeight: 2,
          clickable: false,
        });
      } else {
        circleRef.current.setCenter(center);
        circleRef.current.setRadius(r);
        circleRef.current.setMap(mapRef.current);
      }
      return;
    }
    if (engineRef.current === "leaflet" && leafletRef.current?.map && window.L) {
      const L = window.L;
      if (circleLeafletRef.current) {
        circleLeafletRef.current.setLatLng([la, ln]);
        circleLeafletRef.current.setRadius(r);
      } else {
        circleLeafletRef.current = L.circle([la, ln], {
          radius: r,
          color: "#4f46e5",
          fillColor: "#4f46e5",
          fillOpacity: 0.18,
          weight: 2,
        }).addTo(leafletRef.current.map);
      }
    }
  }, [radiusM, hasPin, latN, lngN, fallbackLat, fallbackLng]);

  const ensureGoogleMarker = useCallback((maps, position) => {
    const map = mapRef.current;
    if (!map) return;
    if (markerRef.current) {
      markerRef.current.setPosition(position);
      markerRef.current.setVisible(true);
      return;
    }
    markerRef.current = new maps.Marker({
      map,
      position,
      draggable: true,
      animation: maps.Animation?.DROP,
      title: "Arrastra para ajustar la ubicación",
    });
    markerRef.current.addListener("dragend", () => {
      const pos = markerRef.current.getPosition();
      if (!pos || !onCoordsChangeRef.current) return;
      onCoordsChangeRef.current(pos.lat(), pos.lng());
    });
  }, []);

  const initLeaflet = useCallback(
    async (center, withPin) => {
      if (!containerRef.current) return false;
      destroyMap();
      await loadLeaflet();
      const la = withPin ? latN : center.lat;
      const ln = withPin ? lngN : center.lng;
      const createMap = streetView ? createLeafletStreetMap : createLeafletSatelliteMap;
      leafletRef.current = createMap(containerRef.current, {
        lat: la,
        lng: ln,
        zoom: withPin ? zoomConPin : zoomSinPin,
        draggable: true,
        onCoordsChange: (a, b) => onCoordsChangeRef.current?.(a, b),
      });
      engineRef.current = "leaflet";
      setEngineLabel(streetView ? "Calles (OpenStreetMap)" : "Satélite con nombres de calles (modo alternativo)");
      setStatus("ready");
      return true;
    },
    [destroyMap, latN, lngN, fallbackLat, streetView, zoomConPin, zoomSinPin],
  );

  const initGoogle = useCallback(
    async (maps) => {
      if (!containerRef.current) return false;
      mapsNsRef.current = maps;

      const center = hasPin
        ? { lat: latN, lng: lngN }
        : fallbackLat != null && fallbackLng != null
          ? { lat: fallbackLat, lng: fallbackLng }
          : DEFAULT_CENTER;

      const defaultType = streetView
        ? maps.MapTypeId?.ROADMAP || "roadmap"
        : maps.MapTypeId?.HYBRID || "hybrid";
      mapRef.current = new maps.Map(containerRef.current, {
        center,
        zoom: hasPin ? zoomConPin : zoomSinPin,
        mapTypeId: defaultType,
        mapTypeControl: true,
        mapTypeControlOptions: {
          mapTypeIds: streetView
            ? [
                maps.MapTypeId?.ROADMAP || "roadmap",
                maps.MapTypeId?.HYBRID || "hybrid",
                maps.MapTypeId?.SATELLITE || "satellite",
              ]
            : [
                maps.MapTypeId?.HYBRID || "hybrid",
                maps.MapTypeId?.SATELLITE || "satellite",
                maps.MapTypeId?.ROADMAP || "roadmap",
              ],
          style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: maps.ControlPosition.TOP_RIGHT,
        },
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        gestureHandling: "greedy",
        clickableIcons: false,
      });

      mapRef.current.addListener("click", (e) => {
        if (!e.latLng || !onCoordsChangeRef.current) return;
        ensureGoogleMarker(maps, e.latLng);
        onCoordsChangeRef.current(e.latLng.lat(), e.latLng.lng());
      });

      if (hasPin) {
        ensureGoogleMarker(maps, { lat: latN, lng: lngN });
        mapRef.current.panTo({ lat: latN, lng: lngN });
        mapRef.current.setZoom(zoomConPin);
      }

      engineRef.current = "google";
      setEngineLabel("");

      await new Promise((r) => setTimeout(r, 600));
      if (googleMapHasAuthError(containerRef.current)) {
        return false;
      }
      setStatus("ready");
      return true;
    },
    [hasPin, latN, lngN, fallbackLat, fallbackLng, ensureGoogleMarker, zoomConPin, zoomSinPin, streetView],
  );

  useEffect(() => {
    if (!active) {
      destroyMap();
      setStatus("idle");
      setEngineLabel("");
      return undefined;
    }

    let cancelled = false;
    setStatus("loading");
    setErrorMsg("");

    const onAuthFail = () => {
      if (!cancelled) void tryLeafletAfterGoogleFail();
    };
    window.addEventListener("vitalcasa-gmaps-auth-failure", onAuthFail);

    async function tryLeafletAfterGoogleFail() {
      const center =
        hasPin
          ? { lat: latN, lng: lngN }
          : fallbackLat != null && fallbackLng != null
            ? { lat: fallbackLat, lng: fallbackLng }
            : DEFAULT_CENTER;
      try {
        const ok = await initLeaflet(center, hasPin);
        if (!ok && !cancelled) {
          setStatus("error");
          setErrorMsg(
            "Mapa no disponible. En Google Cloud activa «Maps JavaScript API» y permite el dominio app.vitalcasa.com en la clave.",
          );
        }
      } catch {
        if (!cancelled) {
          setStatus("error");
          setErrorMsg("No se pudo cargar el visor de mapa");
        }
      }
    }

    (async () => {
      try {
        const apiKey = await fetchGoogleMapsApiKey();
        const maps = await loadGoogleMapsJs(apiKey);
        if (cancelled || !containerRef.current) return;

        destroyMap();
        const googleOk = await initGoogle(maps);
        if (cancelled) return;
        if (!googleOk) {
          await tryLeafletAfterGoogleFail();
        }
      } catch (err) {
        if (cancelled) return;
        const code = err?.message || "";
        if (code === "maps_auth_failed" || code === "maps_script_failed") {
          await tryLeafletAfterGoogleFail();
        } else {
          await tryLeafletAfterGoogleFail();
        }
      }
    })();

    return () => {
      cancelled = true;
      window.removeEventListener("vitalcasa-gmaps-auth-failure", onAuthFail);
      destroyMap();
    };
  }, [active, hasPin, latN, lngN, fallbackLat, fallbackLng, destroyMap, initGoogle, initLeaflet]);

  useEffect(() => {
    if (status !== "ready") return;
    syncRadiusCircle();
  }, [status, syncRadiusCircle, radiusM, latN, lngN, hasPin]);

  useEffect(() => {
    if (status !== "ready") return;

    if (engineRef.current === "google" && mapRef.current && mapsNsRef.current) {
      const maps = mapsNsRef.current;
      if (hasPin) {
        ensureGoogleMarker(maps, { lat: latN, lng: lngN });
        mapRef.current.panTo({ lat: latN, lng: lngN });
        const z = cityView ? 13 : 19;
        if (mapRef.current.getZoom() < (cityView ? 11 : 17)) mapRef.current.setZoom(z);
      } else if (markerRef.current) {
        markerRef.current.setVisible(false);
      }
      return;
    }

    if (engineRef.current === "leaflet" && leafletRef.current?.marker && hasPin) {
      leafletRef.current.marker.setLatLng([latN, lngN]);
      const z = cityView ? 13 : 17;
      leafletRef.current.map.setView([latN, lngN], Math.max(leafletRef.current.map.getZoom(), z));
    }
  }, [latN, lngN, hasPin, status, ensureGoogleMarker, cityView]);

  useEffect(() => {
    if (status !== "ready" || !streetView) return;
    if (engineRef.current === "google" && mapRef.current && hasPin) {
      mapRef.current.panTo({ lat: latN, lng: lngN });
      if (mapRef.current.getZoom() > zoomConPin) mapRef.current.setZoom(zoomConPin);
    }
    if (engineRef.current === "leaflet" && leafletRef.current?.map && hasPin) {
      leafletRef.current.map.setView([latN, lngN], zoomConPin);
    }
  }, [status, streetView, hasPin, latN, lngN, zoomConPin]);

  const shellStyle = {
    borderRadius: 10,
    overflow: "hidden",
    border: `1px solid ${borderColor}`,
    height: mapHeight,
    background: isDark ? "#0a0e16" : "#e8f0fa",
    position: "relative",
  };

  if (!active) {
    return (
      <div style={shellStyle}>
        <div
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            fontSize: 12,
            color: isDark ? "#94a3b8" : "#64748b",
            textAlign: "center",
          }}
        >
          Completa la dirección para ver el mapa satélite
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={shellStyle}>
        <div ref={containerRef} style={{ width: "100%", height: "100%", zIndex: 0 }} />
        {(status === "loading" || status === "idle") && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: isDark ? "rgba(10,14,22,0.75)" : "rgba(232,240,250,0.85)",
              fontSize: 12,
              color: isDark ? "#94a3b8" : "#64748b",
              zIndex: 2,
            }}
          >
            Cargando mapa…
          </div>
        )}
        {status === "error" && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
              fontSize: 11,
              color: "#ef4444",
              textAlign: "center",
              background: isDark ? "rgba(10,14,22,0.9)" : "rgba(255,255,255,0.92)",
              zIndex: 2,
            }}
          >
            {errorMsg}
          </div>
        )}
        {status === "ready" && !hasPin && (
          <div
            style={{
              position: "absolute",
              left: 8,
              right: 8,
              bottom: 8,
              padding: "6px 10px",
              borderRadius: 6,
              fontSize: 11,
              background: isDark ? "rgba(15,23,42,0.88)" : "rgba(255,255,255,0.92)",
              color: isDark ? "#cbd5e1" : "#475569",
              pointerEvents: "none",
              zIndex: 2,
            }}
          >
            Haz clic en el mapa o espera las coordenadas automáticas
          </div>
        )}
      </div>
      {hasPin && status === "ready" && (
        <p style={{ margin: 0, fontSize: 10, color: isDark ? "#94a3b8" : "#64748b" }}>
          Arrastra el pin o haz clic en el mapa para afinar la ubicación
          {engineLabel ? ` · ${engineLabel}` : ""}
        </p>
      )}
    </div>
  );
}
